import { Delete, Edit, Star, StarBorder } from "@mui/icons-material";
import {
  Breadcrumbs,
  IconButton,
  Link,
  Stack,
  styled,
  Tooltip,
  Typography,
} from "@mui/material";
import { useCallback } from "react";
import { BotChat } from "../../interfaces/chabot";
import { Link as ToolpadLink } from "../Common/Link";
import { useStarChat } from "../Sirius/SiriusQueries";
import DeleteChatDialog from "../useDialogs/DeleteChatDialog";
import RenameChatDialog from "../useDialogs/RenameChatDialog";
import { useDialogs } from "../useDialogs/useDialogs";
import { getItemTitle } from "../useNavigation/navigation";

const PageContentHeader = styled("div")(({ theme }) => ({
  display: "flex",
  flexDirection: "row",
  justifyContent: "space-between",
  gap: theme.spacing(2),
}));

function ChatTitleToolbar(chat: BotChat) {
  const dialogs = useDialogs();
  const { mutate: starMutate } = useStarChat(chat.bot.id);

  const handleFavorite = () => {
    starMutate({ chatId: chat.id, isFavorite: !chat.favourite });
  };

  return (
    <div className="flex items-center">
      <Tooltip
        title={chat.favourite ? "Retirer des favoris" : "Ajouter aux favoris"}
      >
        <IconButton onClick={handleFavorite}>
          {chat.favourite ? <Star sx={{ color: "gold" }} /> : <StarBorder />}
        </IconButton>
      </Tooltip>
      <Tooltip title="Renommer">
        <IconButton
          onClick={async () => {
            await dialogs.open(RenameChatDialog, chat);
          }}
        >
          <Edit />
        </IconButton>
      </Tooltip>
      <Tooltip title="Supprimer">
        <IconButton
          onClick={async () => {
            await dialogs.open(DeleteChatDialog, chat);
          }}
        >
          <Delete />
        </IconButton>
      </Tooltip>
    </div>
  );
}

function ChatTitle({ chat }: { chat: BotChat | undefined }) {
  const PageToolbarComponent = useCallback(
    () => chat && ChatTitleToolbar(chat),
    [chat]
  );

  const breadcrumbs =
    chat && chat.bot.name
      ? [
          { title: chat.bot.name, path: `/sirius/bot/${chat.bot.id}` },
          {
            title: chat.title ?? "Sans titre",
            path: `/sirius/chat/${chat.id}`,
          },
        ]
      : [];

  return (
    <Stack className="px-6 mt-4">
      <Breadcrumbs aria-label="breadcrumb">
        {breadcrumbs
          ? breadcrumbs.map((item, index) => {
              return item.path ? (
                <Link
                  key={index}
                  component={ToolpadLink}
                  underline="hover"
                  color="inherit"
                  href={item.path}
                >
                  {getItemTitle(item)}
                </Link>
              ) : (
                <Typography key={index} color="text.primary">
                  {getItemTitle(item)}
                </Typography>
              );
            })
          : null}
      </Breadcrumbs>

      <PageContentHeader>
        {chat?.title && <Typography variant="h4">{chat.title}</Typography>}
        <PageToolbarComponent />
      </PageContentHeader>
    </Stack>
  );
}

export default ChatTitle;
